import React, { useState } from 'react';
import './Transfer.css';
import { API_URL } from '../config';

// Fixed dropdown -- no canonical section list exists elsewhere in the app
// (the registration screen's own "section" field is free text), so this
// is deliberately hardcoded to the confirmed set of values.
const VOLUNTEER_SECTIONS = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'B3', 'B4'];

// Shown after a successful identity match: the child + any siblings found
// sharing the same phone/email, the branch's static content, the
// once-per-submission volunteer question, and one submit button for the
// whole group -- no per-student selection, per explicit requirement.
const TransferReview = ({ branchCode, code, branchName, reviewText, matchFoundText, successText, alreadySubmittedText, grades, name, studentId, dateOfBirth, phoneLastFour, primaryStudent, siblings }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // Defaults to "No" -- an opt-in question, answering "Yes" is what reveals
  // the three required detail fields below it.
  const [volunteerTeacher, setVolunteerTeacher] = useState(false);
  const [volunteerTeacherName, setVolunteerTeacherName] = useState('');
  const [volunteerGradeId, setVolunteerGradeId] = useState('');
  const [volunteerSection, setVolunteerSection] = useState('');
  // True when the submit call succeeded but created nothing -- every
  // student in the group already has an active (submitted/approved)
  // request, so there's nothing new to confirm. Shown as its own state,
  // not the success screen, since nothing was actually submitted just now.
  const [alreadyPending, setAlreadyPending] = useState(false);

  const allStudents = [primaryStudent, ...siblings];
  const canSubmit = !volunteerTeacher || (volunteerTeacherName.trim() && volunteerGradeId && volunteerSection);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/transfer/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branch: branchCode, code, name, student_id: studentId, date_of_birth: dateOfBirth, phone_last_four: phoneLastFour,
          volunteer_teacher: volunteerTeacher,
          volunteer_teacher_name: volunteerTeacherName.trim(),
          volunteer_grade_id: volunteerGradeId || null,
          volunteer_section: volunteerSection,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if ((data.created || []).length > 0) {
          setDone(true);
        } else {
          setAlreadyPending(true);
        }
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const defaultSuccess = `Your transfer request for ${allStudents.length === 1 ? 'your child' : `all ${allStudents.length} children`} has been submitted to ${branchName}.`;
    return (
      <div className="transfer-container">
        <h1 className="transfer-title">Request Submitted</h1>
        <div className="transfer-success">{successText || defaultSuccess}</div>
      </div>
    );
  }

  if (alreadyPending) {
    const defaultAlready = `${allStudents.length === 1 ? 'A request for this student has' : 'Requests for these students have'} already been submitted to ${branchName} and ${allStudents.length === 1 ? 'is' : 'are'} awaiting review — no need to submit again. The branch will follow up with you.`;
    return (
      <div className="transfer-container">
        <h1 className="transfer-title">Already Submitted</h1>
        <div className="transfer-static-text">{alreadySubmittedText || defaultAlready}</div>
      </div>
    );
  }

  return (
    <div className="transfer-container">
      <h1 className="transfer-title">Confirm Transfer Request</h1>

      {reviewText && <div className="transfer-static-text">{reviewText}</div>}

      <p className="transfer-subtitle">
        {matchFoundText || (allStudents.length === 1
          ? 'We found this student. Submitting will request a transfer for them.'
          : `We found this student and ${allStudents.length - 1} sibling${allStudents.length - 1 > 1 ? 's' : ''} sharing the same contact info. Submitting will request a transfer for all of them together.`)}
      </p>

      {allStudents.map(s => (
        <div className="transfer-student-card" key={s.id}>
          <div className="transfer-student-name">{s.first_name} {s.last_name}</div>
          <div className="transfer-student-meta">ID: {s.id}</div>
        </div>
      ))}

      <div className="transfer-field">
        <label>Are you or your spouse volunteering as a teacher?</label>
        <div className="transfer-radio-group">
          <label>
            <input type="radio" name="volunteer-teacher" checked={!volunteerTeacher} onChange={() => setVolunteerTeacher(false)} /> No
          </label>
          <label>
            <input type="radio" name="volunteer-teacher" checked={volunteerTeacher} onChange={() => setVolunteerTeacher(true)} /> Yes
          </label>
        </div>
      </div>

      {volunteerTeacher && (
        <>
          <div className="transfer-field">
            <label htmlFor="transfer-volunteer-name">Teacher Name</label>
            <input
              id="transfer-volunteer-name"
              type="text"
              value={volunteerTeacherName}
              onChange={e => setVolunteerTeacherName(e.target.value)}
            />
          </div>
          <div className="transfer-field">
            <label htmlFor="transfer-volunteer-grade">Grade</label>
            <select id="transfer-volunteer-grade" value={volunteerGradeId} onChange={e => setVolunteerGradeId(e.target.value)}>
              <option value="">Select grade…</option>
              {(grades || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="transfer-field">
            <label htmlFor="transfer-volunteer-section">Section</label>
            <select id="transfer-volunteer-section" value={volunteerSection} onChange={e => setVolunteerSection(e.target.value)}>
              <option value="">Select section…</option>
              {VOLUNTEER_SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </>
      )}

      {error && <div className="transfer-error">{error}</div>}

      <div className="transfer-actions">
        <button className="transfer-btn" onClick={handleSubmit} disabled={submitting || !canSubmit}>
          {submitting ? 'Submitting…' : 'Submit Transfer Request'}
        </button>
      </div>
    </div>
  );
};

export default TransferReview;
